import { __assign, __rest } from "tslib";
import { useRef } from 'react';
import { useA11yKeyEvent } from '../useA11yKeyEvent';
import { useDebounce } from '../useDebounce';
import { useEventCallback } from '../useEventCallback';
import { useEventListener } from '../useEventListener';
import { useGlobalListener } from '../useGlobalListener';
import { useTouchMouseEvent } from '../useTouchMouseEvent';
export var isTouchEvent = function (ev) {
    return 'touches' in ev;
};
/**
 * Provide longPress helper, both `click`/`tab`/`keydown` will trigger event
 *
 * - Trigger `onTap` when user action leave small than delay time.
 * - Trigger `onPress` when action time is long than delay time.
 *
 * @default UseLongPressOptions { delay = 300 }
 */
export var useLongPress = function (_a, _b, _c) {
    var onTap = _a.onTap, onPress = _a.onPress;
    var _d = _b === void 0 ? {} : _b, onTouchStartArg = _d.onTouchStart, onTouchEndArg = _d.onTouchEnd, onMouseDown = _d.onMouseDown, onKeyDown = _d.onKeyDown, onKeyUp = _d.onKeyUp, onMouseUpArg = _d.onMouseUp;
    var _e = _c === void 0 ? {} : _c, _f = _e.isPreventDefault, isPreventDefault = _f === void 0 ? true : _f, _g = _e.delay, delay = _g === void 0 ? 300 : _g, _h = _e.externalWindow, externalWindow = _h === void 0 ? window : _h;
    var isEmittedRef = useRef(false);
    var isA11yDownRef = useRef(false);
    var actionRef = useRef(null);
    var reasonRef = useRef('click');
    var emitLongPress = useDebounce(function (e) {
        // * only when have onPress set have emit
        if (onPress) {
            onPress(e, reasonRef.current);
            isEmittedRef.current = true;
        }
    }, delay);
    var end = function (e, isInside) {
        // ! mouse up only trigger when up on element, so we host that in document
        globalMouseUpListener.remove();
        if (!isEmittedRef.current && isInside) {
            onTap === null || onTap === void 0 ? void 0 : onTap(e, reasonRef.current);
        }
        reasonRef.current = 'click';
        isEmittedRef.current = false;
        emitLongPress.cancel();
    };
    var start = function (e) {
        // ! mouse up only trigger when up on element, so we host that in document
        globalMouseUpListener.listen();
        var isTouch = e.type === 'touchstart';
        if (isTouch) {
            reasonRef.current = 'tap';
        }
        emitLongPress(e);
    };
    var _j = useTouchMouseEvent({
        onTouchStart: function (e) {
            // prevent long touch leave element focus
            if (isPreventDefault) {
                e.preventDefault();
            }
            onTouchStartArg === null || onTouchStartArg === void 0 ? void 0 : onTouchStartArg(e);
            start(e);
        },
        onTouchEnd: function (e, isInside) {
            // prevent leave element focus
            if (isPreventDefault) {
                e.preventDefault();
            }
            onTouchEndArg === null || onTouchEndArg === void 0 ? void 0 : onTouchEndArg(e, isInside);
            end(e, isInside);
        },
        onMouseDown: function (e) {
            if (isPreventDefault) {
                e.preventDefault();
            }
            onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(e);
            start(e);
        },
        onMouseUp: function (e, isInside) {
            // prevent leave element focus
            if (isPreventDefault) {
                e.preventDefault();
            }
            onMouseUpArg === null || onMouseUpArg === void 0 ? void 0 : onMouseUpArg(e);
            end(e, isInside);
        },
    }, { actionRef: actionRef }), onMouseUp = _j.onMouseUp, onTouchEnd = _j.onTouchEnd, onTouchStart = _j.onTouchStart, events = __rest(_j, ["onMouseUp", "onTouchEnd", "onTouchStart"]);
    // use event listener for make event `passive` work in safari
    useEventListener(actionRef, 'touchstart', onTouchStart, {
        passive: false,
    });
    // use event listener for make event `passive` work in safari
    useEventListener(actionRef, 'touchend', onTouchEnd, {
        passive: false,
    });
    var globalMouseUpListener = useGlobalListener('mouseup', onMouseUp, {
        target: externalWindow,
        startImmediately: false,
    });
    var handleDeleteKeyDown = useA11yKeyEvent(function (e) {
        reasonRef.current = 'keyboard';
        // * keydown when hold will trigger many time, show when current is a11y down, not trigger again
        if (!isA11yDownRef.current) {
            emitLongPress(e);
        }
        isA11yDownRef.current = true;
    });
    var handleKeyDown = useEventCallback(function (e) {
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        handleDeleteKeyDown(e);
    });
    var handleKeyUp = useEventCallback(function (e) {
        onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(e);
        if (isA11yDownRef.current)
            end(e, true);
        isA11yDownRef.current = false;
    });
    return __assign({ ref: actionRef, onKeyDown: handleKeyDown, onKeyUp: handleKeyUp }, events);
};
